import { readDuracao } from "./nova-obra.duracao";
import { readEquipe } from "./nova-obra.equipe";
import { readTiposObra, tipoDominante } from "./nova-obra.tipos";
import type { TipoObraItem } from "./nova-obra.tipos";
import type { NovaObraState } from "./nova-obra.types";
import type {
  CriarObraCentroCustoPayload,
  CriarObraPayload,
  CriarObraTipoPayload,
} from "@/modules/obras/criar-obra.types";

const textoOuNull = (texto: string) => texto.trim() || null;

/**
 * Monta o corpo do POST com o estado do wizard. Não valida: os campos obrigatórios já foram
 * validados por etapa e o core revalida tudo (400 com a mensagem). Exatamente um tipo recebe
 * `dominante: true`, mesmo que o código se repita em centros de custo diferentes.
 */
export function montarPayloadObra(
  state: NovaObraState,
  justificativaCondicoes: string | null,
): CriarObraPayload {
  const { obra } = state;
  const duracao = readDuracao(state.etapas.duracao);
  const tipos = readTiposObra(state.etapas.tiposObra);
  const equipe = readEquipe(state.etapas.equipe);
  const dominanteUid = tipoDominante(tipos)?.uid ?? null;

  const tipoPayload = (tipo: TipoObraItem): CriarObraTipoPayload => {
    return {
      tipoObraId: tipo.tipoObraId,
      dominante: tipo.uid === dominanteUid,
      cliente: textoOuNull(tipo.cliente),
      contrato: textoOuNull(tipo.contrato),
      inspecaoCasco: tipo.haveraInspecaoCasco ? tipo.inspecaoCasco : null,
      escoposMacro: [],
    };
  };

  // Embarcação sem centro de custo (Impasse 33): sem principal não há onde pendurar os tipos; o
  // core responde 400 ("Informe o centro de custo principal.") e a tela mostra a mensagem.
  const centrosCusto: CriarObraCentroCustoPayload[] =
    tipos.centroCustoPrincipalId === null
      ? []
      : [
          {
            centroCustoId: tipos.centroCustoPrincipalId,
            principal: true,
            tipos: tipos.tipos.map(tipoPayload),
          },
          ...tipos.centrosAdicionais.map((adicional) => ({
            centroCustoId: adicional.centro.id,
            principal: false,
            tipos: adicional.tipos.map(tipoPayload),
          })),
        ];

  return {
    embarcacaoId: obra.embarcacaoId ?? "",
    localPlanejadoId: obra.localPlanejadoId,
    manutencaoCorretiva: obra.manutencaoCorretiva,
    statusObraId: obra.statusObraId ?? "",
    statusFinanceiro: obra.statusFinanceiro ?? "FEL_0",
    observacoes: textoOuNull(obra.observacoes),
    descricao: obra.descricao.trim(),
    inicio: duracao.inicioObra,
    periodos: duracao.periodos.flatMap((periodo) =>
      periodo.periodoObraId !== null && periodo.duracao !== null
        ? [{ periodoObraId: periodo.periodoObraId, duracao: periodo.duracao, meta: periodo.meta }]
        : [],
    ),
    centrosCusto,
    justificativaDominante:
      tipos.dominanteUid !== null ? textoOuNull(tipos.justificativaDominante) : null,
    justificativaCondicoes,
    equipe: equipe.linhas.flatMap((linha) =>
      linha.usuarioId !== null && !linha.editing
        ? [
            {
              papelObraId: linha.papelObraId,
              usuarioId: linha.usuarioId,
              inicio: linha.inicio,
              fim: linha.fim,
            },
          ]
        : [],
    ),
    justificativaEquipe: textoOuNull(equipe.justificativa ?? ""),
  };
}
